export enum ResourceFormat {
  LINK = 'URL',
  VIMEO = 'Video',
  IMAGE = 'Image',
  PDF = 'PDF',
  GOOGLE_STUDIO = 'Google Studio',
  GOOGLE_SLIDES = 'Google Slides',
  READYMAG = 'Readymag',
  EMBEDDABLE_URL = 'Embeddable URL',
  EMBEDDABLE_HTML = 'Embeddable HTML',
  KYUOS_LINK = 'kyu OS',
}

export const EMBEDDABLE_FORMATS = [
  ResourceFormat.VIMEO,
  ResourceFormat.IMAGE,
  ResourceFormat.PDF,
  ResourceFormat.GOOGLE_STUDIO,
  ResourceFormat.GOOGLE_SLIDES,
  ResourceFormat.READYMAG,
  ResourceFormat.EMBEDDABLE_URL,
  ResourceFormat.EMBEDDABLE_HTML,
];

export const AUTO_FULLSCREEN_FORMATS = [
  ResourceFormat.EMBEDDABLE_URL,
  ResourceFormat.EMBEDDABLE_HTML,
];

export type ResourceClickDecision =
  | { type: 'detail'; href: string; opensInNewTab: false }
  | { type: 'detail_new_tab'; href: string; opensInNewTab: true }
  | { type: 'modal'; href: string; opensInNewTab: false };

export type ResourceClickMode = 'default' | 'detail';

export interface ResourceClickEvent {
  button: number;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}

export interface ResourceClickBehaviorInput {
  slug: string;
  open_in_modal?: boolean;
  resource_format_name?: string;
  link?: string;
}
export interface ResourceRouteState {
  suppressAutoFullScreen?: boolean;
}

export interface ResourceViewBehaviorInput {
  resource_format_name?: string;
  link?: string;
  content_html?: string;
  image?: string;
  downloadable?: boolean;
}

export interface ResourceViewBehavior {
  embeddable: boolean;
  hasEmbeddableContent: boolean;
  canExpand: boolean;
  canFullScreen: boolean;
  autoFullScreenOnDetail: boolean;
  canDownload: boolean;
}

export const resolveResourceViewBehavior = ({
  resource_format_name,
  link,
  content_html,
  image,
  downloadable,
}: ResourceViewBehaviorInput): ResourceViewBehavior => {
  const format = resource_format_name as ResourceFormat;
  const embeddable = EMBEDDABLE_FORMATS.includes(format);

  let hasEmbeddableContent = false;

  if (format === ResourceFormat.EMBEDDABLE_HTML) {
    hasEmbeddableContent = !!content_html;
  } else if (format === ResourceFormat.IMAGE) {
    hasEmbeddableContent = !!image;
  } else if (embeddable) {
    hasEmbeddableContent = !!link;
  }

  return {
    embeddable,
    hasEmbeddableContent,
    canExpand: embeddable && hasEmbeddableContent,
    canFullScreen: embeddable && hasEmbeddableContent,
    autoFullScreenOnDetail: AUTO_FULLSCREEN_FORMATS.includes(format) && hasEmbeddableContent,
    canDownload: !!downloadable && !!link,
  };
};

// Keep signed query strings intact; PDF viewer options belong in the fragment.
export const resourcePreviewUrl = ({ resource_format_name, link }: ResourceViewBehaviorInput) => {
  if (resource_format_name !== ResourceFormat.PDF || !link) return link;

  const hashIndex = link.indexOf('#');
  const url = hashIndex === -1 ? link : link.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? '' : link.slice(hashIndex + 1);
  const params = new URLSearchParams(
    fragment && !fragment.includes('=') && !fragment.includes('&')
      ? 'nameddest=' + fragment
      : fragment
  );
  params.set('navpanes', '0');
  params.set('toolbar', '0');
  return url + '#' + params.toString();
};

// Snapshot links use OS handlers without the viewer's temporary access token.
export const requiresAuthenticatedResourcePreview = ({
  resource_format_name,
  link,
}: ResourceViewBehaviorInput) => {
  const format = resource_format_name as ResourceFormat;
  if (
    !EMBEDDABLE_FORMATS.includes(format) ||
    [ResourceFormat.IMAGE, ResourceFormat.EMBEDDABLE_HTML].includes(format)
  ) {
    return false;
  }

  if (!link) return format === ResourceFormat.PDF;

  try {
    const { pathname } = new URL(link, 'https://resource-preview.invalid');
    return ['/file_resource', '/embeddable_resource'].includes(pathname);
  } catch {
    return false;
  }
};

export const resourceDetailPath = (slug: string) => `/resources/${slug}`;
export const resourceNewTabPath = (slug: string) => `${resourceDetailPath(slug)}?resource_tab=1`;

export const resourceFullScreenPath = (slug: string) => `${resourceDetailPath(slug)}/embed`;

export const resolveResourceClick = ({
  slug,
  open_in_modal,
}: ResourceClickBehaviorInput): ResourceClickDecision => {
  const detailHref = resourceDetailPath(slug);

  if (open_in_modal !== false) {
    return {
      type: 'modal',
      href: detailHref,
      opensInNewTab: false,
    };
  }

  return {
    type: 'detail_new_tab',
    href: resourceNewTabPath(slug),
    opensInNewTab: true,
  };
};

export const resolveResourceClickForMode = (
  input: ResourceClickBehaviorInput,
  mode: ResourceClickMode = 'default'
): ResourceClickDecision => {
  if (mode === 'detail') {
    return {
      type: 'detail',
      href: resourceDetailPath(input.slug),
      opensInNewTab: false,
    };
  }

  return resolveResourceClick(input);
};

export const shouldLaunchResourceFullScreenOnDetail = (
  viewBehavior: ResourceViewBehavior,
  routeState: ResourceRouteState = {}
) => viewBehavior.autoFullScreenOnDetail && !routeState.suppressAutoFullScreen;

export const resourceClickOpensInNewTab = (input: ResourceClickBehaviorInput) =>
  resolveResourceClick(input).opensInNewTab;

export const resourceClickLinkProps = (decision: ResourceClickDecision) => ({
  href: decision.href,
  target: decision.opensInNewTab ? '_blank' : undefined,
  rel: decision.opensInNewTab ? 'noopener noreferrer' : undefined,
});

export const shouldUseNativeResourceClick = (
  event: ResourceClickEvent,
  decision: ResourceClickDecision
) =>
  event.button !== 0 ||
  event.altKey ||
  event.ctrlKey ||
  event.metaKey ||
  event.shiftKey ||
  decision.opensInNewTab;
